
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';
import { X, MapPin, Shield, Home, Users, Calendar, DollarSign } from 'lucide-react';

interface Property {
  id: string;
  title: string;
  price_egp: number;
  deposit_egp: number;
  distance_km: number;
  unit_type: string;
  furnished: boolean;
  verified: boolean;
  photos: string[];
  amenities: string[];
  available_from: string;
  lease_min_months: number;
  gender_rules: string;
  universities?: {
    name_en: string;
    name_ar: string;
  };
}

interface PropertyComparisonProps {
  properties: Property[];
  onRemoveProperty: (id: string) => void;
  onClose: () => void;
}

const PropertyComparison: React.FC<PropertyComparisonProps> = ({
  properties,
  onRemoveProperty,
  onClose
}) => {
  const { language, t } = useLanguage();

  const getUnitTypeLabel = (type: string) => {
    const labels = {
      apartment: { en: 'Apartment', ar: 'شقة' },
      room: { en: 'Room', ar: 'غرفة' },
      studio: { en: 'Studio', ar: 'استوديو' },
      house: { en: 'House', ar: 'منزل' }
    };
    return labels[type as keyof typeof labels]?.[language === 'ar' ? 'ar' : 'en'] || type;
  };

  const getGenderLabel = (gender: string) => {
    const labels = {
      any: { en: 'Any', ar: 'أي جنس' },
      female: { en: 'Female Only', ar: 'إناث فقط' },
      male: { en: 'Male Only', ar: 'ذكور فقط' },
      mixed: { en: 'Mixed', ar: 'مختلط' }
    };
    return labels[gender as keyof typeof labels]?.[language === 'ar' ? 'ar' : 'en'] || gender;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-background rounded-lg max-w-6xl w-full max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-background border-b p-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold">
            {t('property_comparison', 'مقارنة العقارات', 'Property Comparison')}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {properties.map((property) => (
              <Card key={property.id} className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2 z-10"
                  onClick={() => onRemoveProperty(property.id)}
                >
                  <X className="h-4 w-4" />
                </Button>

                <CardHeader className="pb-3">
                  <img
                    src={property.photos?.[0] || '/placeholder.svg'}
                    alt={property.title}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <CardTitle className="text-lg leading-tight">
                    {property.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-4">
                  {/* Price */}
                  <div className="bg-primary/5 rounded-lg p-3">
                    <div className="text-2xl font-bold text-primary">
                      {property.price_egp.toLocaleString()} {t('egp', 'ج.م', 'EGP')}
                      <span className="text-sm font-normal text-muted-foreground">
                        /{t('month', 'شهر', 'month')}
                      </span>
                    </div>
                    {property.deposit_egp > 0 && (
                      <div className="text-sm text-muted-foreground">
                        + {property.deposit_egp.toLocaleString()} {t('deposit', 'تأمين', 'deposit')}
                      </div>
                    )}
                  </div>

                  {/* Key Details */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        {property.distance_km}km from {' '}
                        {language === 'ar' ? property.universities?.name_ar : property.universities?.name_en}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Home className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{getUnitTypeLabel(property.unit_type)}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{getGenderLabel(property.gender_rules)}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        {t('available', 'متاح', 'Available')}: {' '}
                        {new Date(property.available_from).toLocaleDateString()}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        {t('min_lease', 'أقل مدة إيجار', 'Min. lease')}: {' '}
                        {property.lease_min_months} {t('months', 'شهور', 'months')}
                      </span>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1">
                    {property.verified && (
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        <Shield className="h-3 w-3 mr-1" />
                        {t('verified', 'موثق', 'Verified')}
                      </Badge>
                    )}
                    {property.furnished && (
                      <Badge variant="outline">
                        {t('furnished', 'مفروش', 'Furnished')}
                      </Badge>
                    )}
                  </div>

                  {/* Amenities */}
                  {property.amenities && property.amenities.length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm mb-2">
                        {t('amenities', 'المرافق', 'Amenities')}
                      </h4>
                      <div className="flex flex-wrap gap-1">
                        {property.amenities.slice(0, 3).map((amenity, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {amenity}
                          </Badge>
                        ))}
                        {property.amenities.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{property.amenities.length - 3}
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {properties.length < 3 && (
            <div className="text-center mt-6 p-6 border-2 border-dashed border-muted rounded-lg">
              <p className="text-muted-foreground">
                {t('add_more_properties', 'أضف المزيد من العقارات للمقارنة', 'Add more properties to compare')}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {t('can_compare_up_to', 'يمكنك مقارنة حتى', 'You can compare up to')} 3 {t('properties', 'عقارات', 'properties')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyComparison;
